import { Navigate, NavLink, Outlet } from "react-router-dom";
import DashLayout from "@/layouts/DashLayout";
import PageTitle from "@/components/PageTitle";
import RoleProtectedPage from "@/layouts/RoleProtectedPage";
import { usePermissions } from "@/hooks/usePermissions";
import { Roles } from "@/redux/services/user/user.type";
import { cn } from "@/lib/utils";
import {
  Package,
  Truck,
  ShoppingBag,
  ClipboardCheck,
  History,
  BarChart3,
  LucideIcon,
} from "lucide-react";

interface InventoryTab {
  to: string;
  label: string;
  icon: LucideIcon;
  // Domain whose view permission this tab requires. Most tabs are inventory;
  // Fournisseurs and Achats belong to the suppliers domain (the API guards
  // purchase orders with PERMISSION_SUPPLIERS).
  domain: "inventory" | "suppliers";
  // Explicit permission value gating the tab. When set it overrides the domain
  // view check — the Rapports tab requires "inventory.reports" so a stock
  // manager isn't automatically trusted with cost/margin figures.
  permission?: string;
  end?: boolean;
}

const TABS: InventoryTab[] = [
  {
    to: "/inventory/ingredients",
    label: "Ingrédients",
    icon: Package,
    domain: "inventory",
  },
  {
    to: "/inventory/suppliers",
    label: "Fournisseurs",
    icon: Truck,
    domain: "suppliers",
  },
  {
    to: "/inventory/purchase-orders",
    label: "Achats",
    icon: ShoppingBag,
    domain: "suppliers",
  },
  {
    to: "/inventory/stock-counts",
    label: "Contrôle de stock",
    icon: ClipboardCheck,
    domain: "inventory",
  },
  {
    to: "/inventory/adjustments",
    label: "Mouvements de stock",
    icon: History,
    domain: "inventory",
  },
  {
    to: "/inventory/reports",
    label: "Rapports",
    icon: BarChart3,
    domain: "inventory",
    permission: "inventory.reports",
  },
];

// Tabs visible to the current user. Full-access users (owners, admin, support,
// primary managers) can view every domain; a permission delegate only sees the
// tabs whose domain they can view (e.g. a suppliers.view-only user sees
// Fournisseurs and Achats).
export const useVisibleInventoryTabs = () => {
  const { can, canView } = usePermissions();
  return TABS.filter((tab) =>
    tab.permission ? can(tab.permission) : canView(tab.domain)
  );
};

// Index redirect for /inventory: lands on the first tab the user can access so
// a suppliers-only delegate isn't bounced to the (forbidden) ingredients tab.
export const InventoryIndexRedirect = () => {
  const visibleTabs = useVisibleInventoryTabs();
  const target = visibleTabs[0]?.to ?? "/no-access";
  return <Navigate to={target} replace />;
};

const InventoryPage = () => {
  const visibleTabs = useVisibleInventoryTabs();

  return (
    <DashLayout>
      <RoleProtectedPage
        allowedRoles={[Roles.manager, Roles.owner]}
        allowedPermissions={["inventory.view", "suppliers.view"]}
      >
        <div className="space-y-6">
          <PageTitle
            title="Inventaire"
            description="Gestion des ingrédients, fournisseurs, achats et rapports"
          />

          <nav className="border-b border-border overflow-x-auto -mx-4 sm:mx-0">
            <div className="flex gap-1 min-w-max px-4 sm:px-0">
              {visibleTabs.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap",
                      isActive
                        ? "border-primary text-primary"
                        : "border-transparent text-muted-foreground hover:text-card-foreground hover:border-border"
                    )
                  }
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </NavLink>
              ))}
            </div>
          </nav>

          <Outlet />
        </div>
      </RoleProtectedPage>
    </DashLayout>
  );
};

export default InventoryPage;
